import { useEffect, useState } from "react"
import { generatePath, Link, useNavigate, useParams } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, Flex, Form, Select, Typography } from "antd"
import { choose } from "choose-matching"

import { balancesStore } from "@src/app/balanceStore"
import { ChooseCurrency } from "@src/app/components/ChooseCurrency"
import { InputPattern } from "@src/app/components/InputPattern"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import { NumberPattern } from "@src/app/validators"
import { whitelistStore } from "@src/app/whitelistStore"
import { FormOTP } from "@src/shared/componemts/FormOTP"
import { mergeArrays } from "@src/shared/mergeArrays"
import css from "./index.module.css"

const { Item } = Form

interface Options {
  label: string
  name: string
  network: string
  value: string
}
export const WithdrawPage = observer(() => {
  const { network, symbol } = useParams()

  const [balance, setBalance] = useState("")

  const [withdrawalAvailable, setWithdrawalAvailable] = useState("")

  const [disabledSubmit, setDisabledSubmit] = useState(true)

  const [options, setOptions] = useState<Options[]>([])

  const {
    addFormData,
    balances,
    chooseCurrency,
    chosenNetwork,
    chosenSymbol,
    createWithdrawal,
    currencies,
    hasResponse,
    isLoading: balancesIsLoading,
    resetCurrency,
  } = balancesStore

  const gCurrencies = GlobalCurrenciesCollection()

  const currency = gCurrencies.getBySymbolNetwork({
    network: network as string,
    symbol: symbol as string,
  })

  const { isLoading, whitelist } = whitelistStore

  const [form] = Form.useForm()

  const [mergedBalances, setMergedBalances] = useState(balances)

  const [regex, setRegex] = useState(/^(?!0{2,})\d+(\.\d{1,2})?$/)

  const [isOtpOpen, setIsOtpOpen] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    if (Boolean(balances.length) && Boolean(currencies.length)) {
      setMergedBalances(mergeArrays(balances, currencies))
    }
  }, [balances, currencies])

  useEffect(() => {
    const updateBalance = () => {
      const balance = balances.find(
        (balance) =>
          balance.symbol === (chosenSymbol || symbol) &&
          balance.network === (chosenNetwork || network),
      )

      if (balance) {
        setBalance(balance.balances.available)

        setWithdrawalAvailable(balance.payouts_available.withdrawal)
      }
    }

    const updateOptions = () => {
      const options = whitelist.filter(
        (item) =>
          item.network === (chosenNetwork || network) &&
          item.status === "confirmed",
      )

      if (options.length) {
        setOptions(
          options.map(({ address, id, name, network }) => ({
            label: address,
            name,
            network,
            value: id,
          })),
        )
      } else {
        setOptions([])
      }
    }

    const getDecimal = () => {
      const currency = mergedBalances.find(
        (balance) =>
          balance.symbol === (chosenSymbol || symbol) &&
          balance.network === (chosenNetwork || network),
      )

      if (currency) {
        const { decimals } = gCurrencies.getBySymbolNetwork({
          network: currency.network,
          symbol: currency.symbol,
        })!

        const regex = new RegExp(`^(?!0{2,})\\d+(\\.\\d{1,${decimals}})?$`)

        setRegex(regex)
      }
    }

    updateBalance()

    updateOptions()

    form.resetFields()

    getDecimal()
  }, [whitelist, network, symbol, chosenNetwork, chosenSymbol, mergedBalances])

  useEffect(() => {
    return () => resetCurrency()
  }, [])

  const onsubmitForm = async (value: any) => {
    const { amount, whitelisted_address_id } = value

    addFormData({
      amount,
      symbol: chosenSymbol || symbol,
      whitelisted_address_id,
    })

    setIsOtpOpen(true)
  }

  const handleSelectChange = (value: string) => {
    const [symbol, network] = value.split(" ")

    chooseCurrency(value)

    navigate(
      generatePath(paths.withdraw, {
        network: network as string,
        symbol: symbol as string,
      }),
    )

    setDisabledSubmit(true)
  }

  const setMaxAmount = () => {
    form.setFieldsValue({ amount: withdrawalAvailable })

    form.validateFields(["amount"])
  }

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length)

    const { amount, whitelisted_address_id } = form.getFieldsValue()

    const isValid =
      Boolean(whitelisted_address_id) &&
      Boolean(amount) &&
      amount !== "0" &&
      balance !== "0"

    setDisabledSubmit(hasErrors || !isValid)
  }

  const onOtpSubmit = async (otp: string) => {
    addFormData({ otp })

    return createWithdrawal().then((_) => {
      form.resetFields()
    })
  }

  useEffect(() => {
    if (hasResponse) {
      navigate(paths.withdrawDetails)
    }
  }, [hasResponse])

  return (
    <DefaultPageLayout
      url={paths.balances}
      {...(isOtpOpen
        ? { backAction: () => setIsOtpOpen(false), title: "2FA check" }
        : { title: "Withdraw", url: paths.manualPayouts })}
    >
      <>
        {choose(
          [
            isOtpOpen,
            <FormOTP onSubmit={onOtpSubmit} setVisible={setIsOtpOpen} />,
          ],
          [
            true,
            <Form
              className={css.form}
              form={form}
              id="GENERATED_ID_WITHDRAW_FORM_VOID_1"
              layout="vertical"
              name="withdraw"
              onFieldsChange={handleFormChange}
              onFinish={onsubmitForm}
              size="large"
            >
              <div className={css.formWrapper}>
                <div className={css.left}>
                  <ChooseCurrency
                    currencies={currencies}
                    hasDescription={false}
                    network={network!}
                    onChange={handleSelectChange}
                    symbol={symbol!}
                  />
                  <div className={css.textWrapper}>
                    To transfer funds, choose a whitelisted address. Add a
                    withdrawal address in the Whitelist section of{" "}
                    <Link
                      id="GENERATED_ID_WITHDRAW_LINK_SETTINGS_1"
                      to={`${paths.settings}?tab=al&inner-tab=wlad`}
                    >
                      Settings
                    </Link>
                  </div>
                </div>
                <div className={css.right}>
                  <Item
                    label="Address"
                    name="whitelisted_address_id"
                    rules={[
                      { message: "Please select at least one", required: true },
                    ]}
                  >
                    <Select
                      loading={isLoading}
                      optionRender={(option) => (
                        <div className={css.option}>
                          <div className={css.optionInfoWrapper}>
                            <Typography.Text
                              style={{ color: "#666", fontSize: 12 }}
                            >
                              {option.data.name}
                            </Typography.Text>
                            <span className={css.network}>
                              {option.data.network}
                            </span>
                          </div>
                          <Typography.Text style={{ fontSize: 16 }}>
                            {option.data.label}
                          </Typography.Text>
                        </div>
                      )}
                      options={options}
                    />
                  </Item>
                  <Flex gap={4} vertical>
                    <Item
                      label="Amount"
                      name="amount"
                      rules={[
                        {
                          required: true,
                          validator: (_, value) => {
                            if (Number(value) > Number(withdrawalAvailable)) {
                              return Promise.reject(
                                new Error(
                                  `Maximum withdrawal amount: ${withdrawalAvailable}`,
                                ),
                              )
                            }

                            if (!value.match(regex)) {
                              return Promise.reject(
                                new Error("Invalid number of decimal places"),
                              )
                            }

                            if (Number(withdrawalAvailable) === 0) {
                              return Promise.reject(
                                new Error("Not efficient balance"),
                              )
                            }

                            return Promise.resolve()
                          },
                        },
                      ]}
                    >
                      <InputPattern
                        pattern={NumberPattern}
                        placeholder="0"
                        size="large"
                        suffix={
                          <Typography.Link
                            onClick={setMaxAmount}
                            style={{ padding: 0 }}
                          >
                            MAX
                          </Typography.Link>
                        }
                      />
                    </Item>
                    <Flex vertical>
                      <Typography.Text style={{ opacity: 0.65 }}>
                        Available balance for withdrawal
                      </Typography.Text>
                      <Typography.Text strong style={{ fontSize: 16 }}>
                        {withdrawalAvailable} {currency?.symbol_alias ?? symbol}
                      </Typography.Text>
                    </Flex>
                  </Flex>

                  <Button
                    disabled={disabledSubmit || balancesIsLoading}
                    htmlType="submit"
                    id="GENERATED_ID_WITHDRAW_BUTTON_CONFIRM_1"
                    loading={isLoading}
                    size="large"
                    style={{ margin: "0 auto", width: "fit-content" }}
                    type="primary"
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </Form>,
          ],
        )}
      </>
    </DefaultPageLayout>
  )
})
